import { BusyButton, BusyButtonLink } from '@ztechapps/webui.button'
import { Dropdown } from 'react-bootstrap'
import { DisabledReasonPopOver, IconValueBlock } from '@ztechapps/webui.util'
import type { FunctionComponent } from 'react'
import React from 'react'

export type RowActionMenuProps = {
  icon: string
  label: string
  className?: string
  isBusy?: boolean
  disabled?: boolean
  /** Why the action is blocked — shown on hover and focus; pass it with `disabled`. */
  disabledReason?: string
  text?: string
  onClick: React.MouseEventHandler<HTMLElement>
}

export type RowActionMenuType = 'button' | 'menu'

export const RowActionMenu = (
  props: RowActionMenuProps & { type?: RowActionMenuType; as?: FunctionComponent<RowActionMenuProps> }
) => {
  const { type, as, ...rest } = props
  if (as) {
    const As = as
    return <As {...rest} />
  }

  if (type === 'menu') {
    return <RowActionMenuItem {...rest} />
  } else {
    return <RowActionMenuButtonLink {...rest} />
  }
}

export const RowActionMenuButtonLink = ({
  onClick,
  icon,
  label,
  className,
  disabled,
  disabledReason,
  text,
  isBusy,
}: RowActionMenuProps) => {
  return (
    <BusyButtonLink
      bsSize="lg"
      onClick={onClick}
      icon={icon}
      title={label}
      text={text}
      variant={className ? undefined : 'outline-primary'}
      className={className}
      disabled={disabled}
      disabledReason={disabled ? disabledReason : undefined}
      isBusy={isBusy}
      busyText=" "
    />
  )
}

export const RowActionMenuButton = ({
  onClick,
  icon,
  label,
  className,
  disabled,
  disabledReason,
  text,
  isBusy,
}: RowActionMenuProps) => {
  return (
    <BusyButton
      onClick={onClick}
      icon={icon}
      title={label}
      text={text}
      variant={className ? undefined : 'outline-primary'}
      className={className}
      disabled={disabled}
      disabledReason={disabled ? disabledReason : undefined}
      isBusy={isBusy}
      busyText=" "
    />
  )
}

// @restart/ui overrides `aria-disabled` with `undefined` (its item hook, then its Anchor), so a blocked item — which must stay
// focusable — renders its own anchor that carries it.
const BlockedDropdownItem = React.forwardRef<HTMLAnchorElement, React.ComponentPropsWithoutRef<'a'>>((props, ref) => (
  <a {...props} ref={ref} role="button" href="#" aria-disabled="true" />
))
BlockedDropdownItem.displayName = 'BlockedDropdownItem'

export const RowActionMenuItem = ({
  onClick,
  icon,
  label,
  className,
  disabled,
  disabledReason,
  text,
}: RowActionMenuProps) => {
  const labelOrText = label || text
  // The blocked treatment (stays focusable, swallows clicks, shows the reason) follows BusyButton's blocked branch,
  // libs/webui/button/src/BusyButton/index.tsx:176-205; a dropdown item cannot reuse it.
  const blocked = disabled && Boolean(disabledReason)
  return (
    <DisabledReasonPopOver reason={blocked ? disabledReason : undefined}>
      <Dropdown.Item
        eventKey={labelOrText}
        onClick={blocked ? (event) => event.preventDefault() : onClick}
        disabled={blocked ? undefined : disabled}
        as={blocked ? BlockedDropdownItem : undefined}
        className={blocked ? 'disabled' : undefined}
      >
        <IconValueBlock icon={icon} value={labelOrText} className={blocked || disabled ? undefined : className} />
      </Dropdown.Item>
    </DisabledReasonPopOver>
  )
}
